import { Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { navigation, siteConfig } from '@/lib/marketing-data';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme';
import { cn } from '@/lib/utils';
import {
  Menu,
  X,
  ChevronDown,
  Zap,
  Code2,
  FileText,
  Shield,
} from 'lucide-react';

const productIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  CodeSpringForge: Code2,
  AICodeForge: Zap,
  'SDD-Forge': FileText,
};

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const router = useRouter();

  const isActive = (href: string) => {
    return router.state.location.pathname === href;
  };

  return (
    <header role="banner" className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <nav role="navigation" aria-label="Main navigation" className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold">{siteConfig.name}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-1">
            {navigation.main.map((item) => {
              if (item.name === 'Products') {
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => setProductsOpen(true)}
                    onMouseLeave={() => setProductsOpen(false)}
                  >
                    <button
                      className={cn(
                        'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        isActive(item.href) && 'text-primary'
                      )}
                    >
                      {item.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>

                    {/* Products Dropdown */}
                    {productsOpen && (
                      <div className="absolute left-0 top-full pt-2 w-72">
                        <div className="rounded-lg border bg-popover p-2 shadow-lg">
                          {navigation.products.map((product) => {
                            const Icon =
                              productIcons[product.name] || Code2;
                            return (
                              <Link
                                key={product.name}
                                to={product.href}
                                className={cn(
                                  'flex items-start gap-3 rounded-md p-3 transition-colors',
                                  'hover:bg-accent',
                                  product.comingSoon && 'opacity-60'
                                )}
                              >
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                                  <Icon className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {product.name}
                                    </span>
                                    {product.comingSoon && (
                                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                        Soon
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {product.description}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive(item.href) && 'text-primary'
                  )}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex md:items-center md:gap-3">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm">Start Free</Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden rounded-md p-2 text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div id="mobile-navigation" className="md:hidden border-t border-border py-4">
            <div className="space-y-1">
              {navigation.main.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'block px-3 py-2 text-base font-medium rounded-md transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive(item.href) && 'text-primary bg-accent'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-2 px-3">
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full">
                  Sign In
                </Button>
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full">Start Free</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
